import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { formatDate } from '@angular/common';
import { AgrimotorService } from '../agrimotor.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-ag-fleet-experience',
  templateUrl: './fleet-experience.component.html',
  styleUrls: ['./fleet-experience.component.css', '../../quote.component.css']
})
export class AgrimotorFleetExperienceComponent implements OnInit, OnChanges {
  @Input() staticData;
  @Input() modelObj;
  @Input() authDetails;
  @Input() apiModelObj;
  @Output() tabChanged = new EventEmitter();
  @Output() modelObjUpdated = new EventEmitter();

  submitted = false;

  constructor(private agService: AgrimotorService, private _snackBar: MatSnackBar) { }

  ngOnInit(): void {
    this.ensureAtLeastOneYear();
  }

  /**
   * Every other tab unwraps `application` out of the quote version; this one declared
   * apiModelObj and never used it. So once the parent replaced modelObj with the raw version
   * (which happens on the reload after any save), `modelObj.fleet_experiences` was undefined:
   * the *ngFor rendered nothing and the page showed only "Add Another Year". Saved fleet years
   * never appeared on edit for the same reason.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const application = this.agService.applicationOf(this.apiModelObj);
    if (this.apiModelObj && application) {
      const meta = this.agService.quoteMetaOf(this.apiModelObj);
      // application carries no broker_id — keep the one we already hold so the save below
      // does not post an empty broker.
      const brokerId = this.modelObj?.broker_id;
      this.modelObj = application;
      this.modelObj.id = meta.id;
      this.modelObj.reference = meta.reference;
      this.modelObj.status = meta.status;
      this.modelObj.sequence_number = this.apiModelObj.sequence_number;
      this.modelObj.company_id = this.apiModelObj.company_id;
      this.modelObj.broker_id = this.modelObj.broker_id || this.apiModelObj.broker_id || brokerId;
    }
    this.ensureAtLeastOneYear();
  }

  /** Year 1 is always on screen — the broker should never have to click "Add Another Year"
   *  just to reach the first input. */
  private ensureAtLeastOneYear() {
    if (!this.modelObj) { return; }
    if (!Array.isArray(this.modelObj.fleet_experiences)) {
      this.modelObj.fleet_experiences = [];
    }
    if (this.modelObj.fleet_experiences.length === 0) {
      this.addYear();
    }
  }

  private emptyYear() {
    return {
      start_date: '', end_date: '', years: null, claims: null,
      paid: { own_damage: null, fire_theft: null, third_party: null },
      outstanding: { own_damage: null, fire_theft: null, third_party: null },
      details: ''
    };
  }

  /**
   * What the template iterates, instead of reading modelObj.fleet_experiences directly.
   *
   * Seeding Year 1 from the lifecycle hooks alone was not enough: the parent hands this
   * component a modelObj that already holds a year, and the API's empty fleet_experiences
   * array arrives later — so by the time the list was empty, neither ngOnInit nor
   * ngOnChanges had any reason to run again and the page rendered with no year at all.
   *
   * Evaluating the invariant at render time makes it hold no matter when or how the list
   * is emptied. It returns the real model array (never a copy), so ngModel still writes
   * straight through to what nextTab() posts.
   */
  get fleetYears(): any[] {
    if (!this.modelObj) { return []; }
    if (!Array.isArray(this.modelObj.fleet_experiences) || this.modelObj.fleet_experiences.length === 0) {
      this.modelObj.fleet_experiences = [this.emptyYear()];
    }
    this.syncEndDates(this.modelObj.fleet_experiences);
    return this.modelObj.fleet_experiences;
  }

  addYear() {
    if (!Array.isArray(this.modelObj.fleet_experiences)) {
      this.modelObj.fleet_experiences = [];
    }
    if (this.modelObj.fleet_experiences.length < 5) {
      this.modelObj.fleet_experiences.push(this.emptyYear());
    }
  }

  removeYear(index) {
    this.modelObj.fleet_experiences.splice(index, 1);
  }

  // A policy year runs start → start + 1 year MINUS a day (18/06/2026 → 17/06/2027).
  // Adding a clean year produced "1 year and 1 day" periods.
  private endDateFor(startDate: any): string {
    if (!startDate) { return ''; }
    const start = new Date(startDate);
    if (isNaN(start.getTime())) { return ''; }
    const end = new Date(start.getFullYear() + 1, start.getMonth(), start.getDate() - 1);
    return formatDate(end, 'yyyy-MM-dd', 'en-US');
  }

  calculateEndDate(fleet) {
    fleet.end_date = this.endDateFor(fleet.start_date);
  }

  /**
   * end_date is posted on save but the agri-api does not return it, so a reopened quote had a
   * start date and a blank End Date. It is derived from start_date, never typed (the input is
   * readonly), so recomputing it on read restores it exactly rather than guessing.
   *
   * Only writes when the value actually differs, so this stays idempotent when change
   * detection calls it repeatedly.
   */
  private syncEndDates(years: any[]) {
    years.forEach(fleet => {
      const expected = this.endDateFor(fleet.start_date);
      if (fleet.end_date !== expected) { fleet.end_date = expected; }
    });
  }

  getPaidTotal(fleet) {
    return (fleet.paid.own_damage || 0) + (fleet.paid.fire_theft || 0) + (fleet.paid.third_party || 0);
  }

  getOutstandingTotal(fleet) {
    return (fleet.outstanding.own_damage || 0) + (fleet.outstanding.fire_theft || 0) + (fleet.outstanding.third_party || 0);
  }

  getGrandTotal(fleet) {
    return this.getPaidTotal(fleet) + this.getOutstandingTotal(fleet);
  }

  isFieldMissing(fleet, field: 'start_date' | 'years' | 'claims'): boolean {
    const value = fleet[field];
    return value === null || value === undefined || value === '';
  }

  // Vehicle years and reported claims are counts — negatives are never valid.
  isFieldNegative(fleet, field: 'years' | 'claims'): boolean {
    const value = fleet[field];
    return value !== null && value !== undefined && value !== '' && Number(value) < 0;
  }

  // The <input type="number" min="0"> attribute alone doesn't stop a typed "-1",
  // so clamp on change as well.
  clampToZero(fleet, field: 'years' | 'claims') {
    if (this.isFieldNegative(fleet, field)) { fleet[field] = 0; }
  }

  private sumBreakdown(group: any): number {
    if (!group) { return 0; }
    return (Number(group.own_damage) || 0) + (Number(group.fire_theft) || 0) + (Number(group.third_party) || 0);
  }

  // Once a year reports one or more claims, the paid/outstanding breakdown has to
  // account for them — an all-zero table means the claim amounts were never entered.
  isBreakdownMissing(fleet): boolean {
    if (!(Number(fleet.claims) >= 1)) { return false; }
    return (this.sumBreakdown(fleet.paid) + this.sumBreakdown(fleet.outstanding)) <= 0;
  }

  /**
   * Also called by the parent before it lets the user leave this tab — a reopened quote
   * unlocks every tab, so the header strip is a second way off this page that has to run
   * the same checks as Proceed.
   */
  hasMissingRequiredFields(): boolean {
    // fleetYears (not modelObj.fleet_experiences) — it is what the page renders, and it
    // guarantees the Year 1 that has to be filled in exists to be judged.
    return this.fleetYears.some(f =>
      this.isFieldMissing(f, 'start_date') || this.isFieldMissing(f, 'years') || this.isFieldMissing(f, 'claims')
      || this.isFieldNegative(f, 'years') || this.isFieldNegative(f, 'claims')
      || this.isBreakdownMissing(f)
    );
  }

  /** Turns on the red highlighting without going through Proceed. */
  showValidationErrors() {
    this.submitted = true;
  }

  nextTab() {
    this.submitted = true;
    if (this.hasMissingRequiredFields()) {
      const breakdownMissing = this.fleetYears.some(f => this.isBreakdownMissing(f));
      this._snackBar.open(
        breakdownMissing
          ? 'Please complete the claims breakdown for every year that reports one or more claims.'
          : 'Please complete Start Date, Vehicle Years and Number of Reported Claims for every year.',
        '', { duration: 5000, panelClass: 'text-danger' });
      return;
    }

    let data = {
      data: {
        type: this.modelObj.type,
        attributes: this.modelObj,
        id: this.modelObj.id,
        broker_id: this.modelObj.broker_id,
        sequence_number: this.modelObj.sequence_number,
      }
    };

    this.agService.saveQuote(data)
      .subscribe(
        (res: any) => {
          const ratingType = this.modelObj.rating_type;
          if (res.success) {
            const version = res.result?.quote_data?.quote_versions?.[0];
            const savedApplication = this.agService.applicationOf(version);
            if (version && savedApplication) {
              // Merge (not replace) — a save response that omits a field must not
              // erase data already entered in another tab (e.g. proposer details).
              this.modelObj = { ...this.modelObj, ...savedApplication };
              this.modelObj.id = this.agService.quoteMetaOf(version).id;
              this.modelObj.broker_id = version.broker_id;
              this.modelObj.sequence_number = version.sequence_number;
              this.modelObj.company_id = version.company_id;
            } else if (res.result && res.result.id) {
              this.modelObj.id = res.result.id;
              if (res.result.sequence_number) { this.modelObj.sequence_number = res.result.sequence_number; }
            }
          } else {
            this._snackBar.open(res.message, '', { duration: 5000, panelClass: 'text-danger' });
          }
          if (!this.modelObj.rating_type) { this.modelObj.rating_type = ratingType; }
          // modelObjUpdated already advances the wizard — emitting tabChanged as well
          // would skip a tab.
          this.modelObjUpdated.emit(this.modelObj);
        },
        (err) => {
          this._snackBar.open('Error saving fleet experience. Please try again.', '', { duration: 5000, panelClass: 'text-danger' });
        }
      );
  }

  prevTab() {
    this.tabChanged.emit(0);
  }
}
